/**
 * WorkspaceConfigDrawer
 *
 * Drawer lateral onde o operador escolhe quais acoes aparecem como tags no
 * AI Workspace. As acoes que estao em ALWAYS_IN_MENU (Chamar Agente,
 * Mensagens Prontas, Etiqueta, Followup, Comentario) seguem acessiveis pelo
 * menu de 3 pontinhos do chat mesmo desligadas aqui — informamos isso na
 * descricao de cada item para o operador entender o trade-off.
 */
import React from 'react';
import {
  X, Settings, Languages, Sparkles, MessageSquarePlus,
  BookmarkPlus, Tags, Calendar, MessageCircle, Pin, Shuffle,
} from 'lucide-react';
import {
  WORKSPACE_ACTION_KEYS, ALWAYS_IN_MENU, rouletteKey,
} from './hooks/useWorkspaceConfig';

const ACTION_META = [
  {
    key: WORKSPACE_ACTION_KEYS.TRANSLATE,
    label: 'Traduzir',
    description: 'Traduz o texto digitado no input para o idioma do contato.',
    Icon: Languages,
  },
  {
    key: WORKSPACE_ACTION_KEYS.GENERATE,
    label: 'Resposta Rápida',
    description: 'Gera uma resposta com base nas mensagens selecionadas.',
    Icon: Sparkles,
  },
  {
    key: WORKSPACE_ACTION_KEYS.CUSTOM,
    label: 'Chamar o Agente',
    description: 'Conversa livre com o agente sobre essa conversa.',
    Icon: MessageSquarePlus,
  },
  {
    key: WORKSPACE_ACTION_KEYS.SAVED,
    label: 'Mensagens Prontas',
    description: 'Mensagens salvas e templates Meta aprovados.',
    Icon: BookmarkPlus,
  },
  {
    key: WORKSPACE_ACTION_KEYS.LABEL,
    label: 'Mover de etapa',
    description: 'Move o contato de etapa no funil (ou tira dele).',
    Icon: Tags,
  },
  {
    key: WORKSPACE_ACTION_KEYS.FOLLOWUP,
    label: 'Agendar Followup',
    description: 'Agenda uma mensagem futura para o contato.',
    Icon: Calendar,
  },
  {
    key: WORKSPACE_ACTION_KEYS.FEEDBACK,
    label: 'Adicionar Comentário',
    description: 'Registra observacao interna sobre o contato.',
    Icon: MessageCircle,
  },
];

const Toggle = ({ checked, onChange, colors }) => (
  <button
    type="button"
    onClick={onChange}
    role="switch"
    aria-checked={checked}
    style={{
      position: 'relative',
      width: 36, height: 20, padding: 0, border: 'none',
      borderRadius: 999, cursor: 'pointer', flexShrink: 0,
      backgroundColor: checked ? colors.accentPrimary : colors.bgTertiary,
      transition: 'background-color 0.15s ease',
    }}
  >
    <span style={{
      position: 'absolute', top: 2, left: checked ? 18 : 2,
      width: 16, height: 16, borderRadius: '50%',
      backgroundColor: '#fff',
      transition: 'left 0.15s ease',
      boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
    }} />
  </button>
);

const WorkspaceConfigDrawer = ({
  isOpen, onClose, colors, config, onToggle, transferRoulettes = [],
}) => {
  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1000,
        display: 'flex', alignItems: 'stretch', justifyContent: 'flex-end',
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: colors.bgPrimary,
          width: 420, maxWidth: '100%', height: '100%',
          boxShadow: '-8px 0 32px rgba(0,0,0,0.25)',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '16px 20px', borderBottom: `1px solid ${colors.borderColor}`,
          flexShrink: 0,
        }}>
          <div>
            <h3 style={{
              margin: 0, fontSize: 16, fontWeight: 600, color: colors.textPrimary,
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
              <Settings size={18} color={colors.accentPrimary} />
              Configurar Workspace
            </h3>
            <p style={{ margin: '2px 0 0', fontSize: 12, color: colors.textSecondary }}>
              Escolha quais ações ficam no AI Workspace.
            </p>
          </div>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: colors.textSecondary, padding: 8, borderRadius: 8,
          }}>
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div style={{
          flex: 1, overflowY: 'auto', padding: '12px 16px',
          display: 'flex', flexDirection: 'column', gap: 8,
        }}>
          {ACTION_META.map(({ key, label, description, Icon }) => {
            const isOn = !!config[key];
            const pinnedInMenu = ALWAYS_IN_MENU.has(key);
            return (
              <div
                key={key}
                style={{
                  display: 'flex', alignItems: 'flex-start', gap: 12,
                  padding: '12px 14px', borderRadius: 10,
                  backgroundColor: colors.bgSecondary,
                  border: `1px solid ${colors.borderColor}`,
                }}
              >
                <div style={{
                  width: 32, height: 32, borderRadius: 8, flexShrink: 0,
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  backgroundColor: `${colors.accentPrimary}18`,
                  color: colors.accentPrimary,
                }}>
                  <Icon size={16} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2,
                  }}>
                    <span style={{
                      fontSize: 13, fontWeight: 600, color: colors.textPrimary,
                    }}>
                      {label}
                    </span>
                    {pinnedInMenu && (
                      <span
                        title="Sempre acessível pelo menu de 3 pontinhos do chat"
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: 3,
                          padding: '1px 6px', borderRadius: 10,
                          backgroundColor: colors.bgTertiary,
                          color: colors.textTertiary,
                          fontSize: 10, fontWeight: 500,
                        }}
                      >
                        <Pin size={9} /> No menu
                      </span>
                    )}
                  </div>
                  <p style={{
                    margin: 0, fontSize: 12, color: colors.textSecondary, lineHeight: 1.4,
                  }}>
                    {description}
                  </p>
                </div>
                <Toggle
                  checked={isOn}
                  onChange={() => onToggle(key)}
                  colors={colors}
                />
              </div>
            );
          })}

          {/* Roletas de transferencia — grupos criados pelo admin. O operador
              fixa quais aparecem como chip na barra do workspace. */}
          {transferRoulettes.length > 0 && (
            <>
              <div style={{
                marginTop: 4, padding: '2px 2px 0',
                fontSize: 11, fontWeight: 700, color: colors.textTertiary,
                textTransform: 'uppercase', letterSpacing: '0.4px',
              }}>
                Roletas de transferência
              </div>
              {transferRoulettes.map((group) => {
                const key = rouletteKey(group.id);
                const isOn = !!config[key];
                const count = group.member_count ?? (group.pool || []).length;
                return (
                  <div
                    key={group.id}
                    style={{
                      display: 'flex', alignItems: 'flex-start', gap: 12,
                      padding: '12px 14px', borderRadius: 10,
                      backgroundColor: colors.bgSecondary,
                      border: `1px solid ${colors.borderColor}`,
                    }}
                  >
                    <div style={{
                      width: 32, height: 32, borderRadius: 8, flexShrink: 0,
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      backgroundColor: `${colors.accentPrimary}18`,
                      color: colors.accentPrimary,
                    }}>
                      <Shuffle size={16} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontSize: 13, fontWeight: 600, color: colors.textPrimary,
                        marginBottom: 2, overflow: 'hidden',
                        textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        {group.name}
                      </div>
                      <p style={{
                        margin: 0, fontSize: 12, color: colors.textSecondary, lineHeight: 1.4,
                      }}>
                        Distribui o contato por rodízio entre {count}{' '}
                        {count === 1 ? 'membro' : 'membros'}.
                      </p>
                    </div>
                    <Toggle
                      checked={isOn}
                      onChange={() => onToggle(key)}
                      colors={colors}
                    />
                  </div>
                );
              })}
            </>
          )}
        </div>

        {/* Footer hint */}
        <div style={{
          padding: '10px 16px', borderTop: `1px solid ${colors.borderColor}`,
          fontSize: 11, color: colors.textTertiary, lineHeight: 1.4,
          flexShrink: 0,
        }}>
          As ações marcadas com <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 3,
            padding: '0 4px', borderRadius: 8,
            backgroundColor: colors.bgTertiary, color: colors.textTertiary,
          }}><Pin size={9} /> No menu</span> continuam disponíveis pelo menu de 3 pontinhos do chat mesmo quando desligadas aqui.
        </div>
      </div>
    </div>
  );
};

export default WorkspaceConfigDrawer;
